<script setup lang="ts">
import type { ComputedRef } from "vue";
import type { QuickActionButtonConfigI } from "~/core/models/QuickActionsButtonConfig.interface";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinListDisplayContext } from "~/modules/dtwin/models/DtwinListDisplayContext.enum";
import ILxcFolderPlus from "~icons/lxc/folder-plus";
import ILxcTrash2 from "~icons/lxc/trash-2";

const { t } = useI18n();

const props = defineProps<{
  context?: DtwinListDisplayContext;
  isRemovingFromFleet?: boolean;
  notInFleetUid?: string;
  selectedDtwins: Dtwin[];
  canDeleteDtwins?: boolean;
  organizationCode?: number;
}>();

const emit = defineEmits([
  "removeFromFleet",
  "cancelSelection",
  "deleteDtwins",
]);

const fleetFormShown: Ref<boolean> = ref(false);

const quickActionButtonConfig: ComputedRef<QuickActionButtonConfigI> = computed(
  () => {
    let quickActionButtonConfig: QuickActionButtonConfigI;
    switch (props.context) {
      case DtwinListDisplayContext.FLEET:
        quickActionButtonConfig = {
          icon: ILxcTrash2,
          title: t("dtwins.fleet.removeFromFleet"),
          event: () => emit("removeFromFleet", props.selectedDtwins),
        };
        break;
      case DtwinListDisplayContext.DTWINS:
      default:
        quickActionButtonConfig = {
          icon: ILxcFolderPlus,
          title: t("dtwins.fleet.addToFleet"),
          event: () => (fleetFormShown.value = true),
        };
        break;
    }
    return quickActionButtonConfig;
  },
);

function onUpdateFleetFormShown(show: boolean) {
  fleetFormShown.value = show;
}

function onCancelSelection() {
  emit("cancelSelection");
}

const selectedDtwinUids = computed(() =>
  props.selectedDtwins.map((dtwin) => dtwin.uid),
);
</script>
<template>
  <quick-actions-toolbar
    :quick-action-button-config="quickActionButtonConfig"
    :selection-message="
      t('dtwins.fleet.selectedDevices', selectedDtwins.length)
    "
    @cancel-selection="onCancelSelection"
  >
    <template
      v-if="context === DtwinListDisplayContext.DTWINS && canDeleteDtwins"
    >
      <dtwin-action-delete
        :dtwin-uids="selectedDtwinUids"
        :organization-code="organizationCode"
        @delete="emit('deleteDtwins')"
      />
    </template>
  </quick-actions-toolbar>
  <fleet-form
    v-if="context === DtwinListDisplayContext.DTWINS"
    :fleet-form-shown="fleetFormShown"
    :dtwin-list="selectedDtwins"
    @update:fleet-form-shown="onUpdateFleetFormShown"
    @attached-to-fleet="onCancelSelection"
  />
</template>
